import uniqBy from "lodash-es/uniqBy";
import { useMemo } from "react";
import { useWindowSize } from "react-use";

import { emptyColumn } from "constants/modelCompareData/data/products/emptyColumn";
import { useModelCompareTranslation } from "constants/modelCompareData/translations/useModelCompareTranslation";import type {
  MutatedProduct,
  MutatedProperty,
  ProductId,
} from "../../../constants/modelCompareData/types";
import { useMenu } from "@ubnt/store.core/lib/contexts/MenuContext";
import { TABLE_COLUMN_WIDTH } from "../constants";
import { MOBILE_BREAKPOINT } from "./CompareTable.styles";
import type { CompareTableProps } from "./CompareTable.types";
import { CompareTableMarkup } from "./CompareTableMarkup/CompareTableMarkup";
import {
  createEmptyGridItems,
  filterProducts,
  transformProductForCompare,
} from "./helpers";
import { getNumberOfColumns } from "./helpers/getNumberOfColumns";

const MAX_PRODUCTS_WITHOUT_EMPTY_COLUMNS = 5;

export const CompareTable = ({
  headerStickOffset,
  overlayTopOffset,
  showBuyButton,
  onLinkClick,
  linkToStoreTitleClick,
  additionalProductData,
  selectedProducts,
  setSelectedProducts,
  section,
  products,
  hasBeenObserved,
  selectedProductsLimit,
  setExternalSelectedProducts,
  isOverviewPage,
  isStaticTable,
}: CompareTableProps) => {
  const { formatMessage } = useModelCompareTranslation();
  const { width: screenWidth } = useWindowSize();
  const { storeId } = useMenu();

  const mutatedProducts = useMemo(
    (): MutatedProduct[] =>
      products
        .filter((product) => filterProducts(product, selectedProducts))
        .map((product) =>
          transformProductForCompare({
            product,
            storeId,
            formatMessage,
            additionalProductData,
          }),
        ),
    [additionalProductData, formatMessage, products, storeId, selectedProducts],
  );

  const productsToBeRenderedInTable = useMemo(
    () => uniqBy(mutatedProducts, "id"),
    [mutatedProducts],
  );

  /**
   * Products sorted by selectedProduct order
   */
  const productOrder = useMemo(
    () => selectedProducts.map((product) => product.productId),
    [selectedProducts],
  );

  const sortedProducts = useMemo(
    () =>
      productsToBeRenderedInTable.sort(
        (a: MutatedProduct, b: MutatedProduct) =>
          productOrder.indexOf(a.id as ProductId) -
          productOrder.indexOf(b.id as ProductId),
      ),
    [productOrder, productsToBeRenderedInTable],
  );

  /**
   * Append empty columns to sorted products
   */
  const numberOfColumns = useMemo(
    () =>
      getNumberOfColumns({ selectedProducts, selectedProductsLimit, products }),
    [selectedProducts, selectedProductsLimit, products],
  );

  const numberOfEmptyColumns = useMemo(
    () =>
      isStaticTable ||
      section.products.length === MAX_PRODUCTS_WITHOUT_EMPTY_COLUMNS
        ? 0
        : numberOfColumns - productsToBeRenderedInTable.length,
    [
      numberOfColumns,
      productsToBeRenderedInTable.length,
      section.products.length,
      isStaticTable,
    ],
  );

  const emptyColumns: MutatedProduct[] = useMemo(
    () =>
      createEmptyGridItems({
        emptyItem: emptyColumn,
        numberOfEmptyColumns,
        additionalProductData,
        storeId,
        isMobile: screenWidth < MOBILE_BREAKPOINT,
      }),
    [numberOfEmptyColumns, additionalProductData, storeId, screenWidth],
  );

  const productsWithEmptyGridItems: MutatedProduct[] = useMemo(
    () => [...sortedProducts, ...emptyColumns],
    [sortedProducts, emptyColumns],
  );

  const properties = useMemo(
    (): MutatedProperty[] =>
      section.properties.map((property) => ({
        ...property,
        title: formatMessage(property.title),
        tooltip: property.tooltip && formatMessage(property.tooltip),
      })),
    [formatMessage, section.properties],
  );

  if (!section) {
    return null;
  }

  return (
    <CompareTableMarkup
      products={productsWithEmptyGridItems}
      properties={properties}
      columnWidth={TABLE_COLUMN_WIDTH}
      propertyColumnWidth={section.propertyColumnWidth}
      headerStickOffset={headerStickOffset}
      overlayTopOffset={overlayTopOffset}
      showBuyButton={showBuyButton}
      onLinkClick={onLinkClick}
      linkToStoreTitleClick={linkToStoreTitleClick}
      showBadges={section.showBadges}
      selectedProducts={selectedProducts}
      setSelectedProducts={setSelectedProducts}
      setExternalState={setExternalSelectedProducts}
      hasBeenObserved={hasBeenObserved}
      selectedProductsLimit={selectedProductsLimit}
      currAvailableProducts={products}
      isOverviewPage={isOverviewPage}
      isStaticTable={isStaticTable}
    />
  );
};
